document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const announcementBar = document.querySelector('.announcement');
const siteHeader = document.querySelector('.site-header');
announcementBar?.parentElement?.classList.add('announcement-wrapper');
siteHeader?.parentElement?.classList.add('site-header-wrapper');

const syncStickyHeaderOffset = () => {
  document.documentElement.style.setProperty('--ea-announcement-height', `${announcementBar?.offsetHeight || 0}px`);
};
syncStickyHeaderOffset();
window.addEventListener('resize', syncStickyHeaderOffset, { passive: true });

const buildScopedProductQuery = (query, productTypes = '') => {
  const types = productTypes.split('|').filter(Boolean);
  if (!query || !types.length) return query;

  const typeQuery = types
    .map((type) => `product_type:"${type.replaceAll('"', '\\"')}"`)
    .join(' OR ');
  return `${query} AND (${typeQuery})`;
};

class EADrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.backdrop = this.querySelector('[data-drawer-backdrop]');
    this.trigger?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');
    this.closeButton?.focus();
  }

  close() {
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.trigger?.focus();
  }
}

customElements.define('ea-drawer', EADrawer);

const megaMenus = Array.from(document.querySelectorAll('.nav-mega'));
const megaMenuCloseTimers = new WeakMap();

const cancelMegaMenuClose = (menu) => {
  const timer = megaMenuCloseTimers.get(menu);
  if (timer) {
    window.clearTimeout(timer);
    megaMenuCloseTimers.delete(menu);
  }
};

const closeOtherMegaMenus = (currentMenu) => {
  megaMenus.forEach((menu) => {
    if (menu !== currentMenu) {
      cancelMegaMenuClose(menu);
      menu.removeAttribute('open');
    }
  });
};

megaMenus.forEach((menu) => {
  menu.addEventListener('toggle', () => {
    if (menu.open) closeOtherMegaMenus(menu);
  });

  const item = menu.closest('.site-header__nav-item');
  item?.addEventListener('mouseenter', () => {
    cancelMegaMenuClose(menu);
    closeOtherMegaMenus(menu);
    menu.setAttribute('open', '');
  });
  item?.addEventListener('mouseleave', () => {
    cancelMegaMenuClose(menu);
    const timer = window.setTimeout(() => {
      menu.removeAttribute('open');
      megaMenuCloseTimers.delete(menu);
    }, 280);
    megaMenuCloseTimers.set(menu, timer);
  });

  menu.querySelector('.nav-mega__panel')?.addEventListener('mouseenter', () => {
    cancelMegaMenuClose(menu);
    menu.setAttribute('open', '');
  });
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('.nav-mega')) closeOtherMegaMenus(null);
});

class EASearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.form = this.querySelector('form');
    this.queryField = this.querySelector('[data-search-query]');
    this.abortController = null;

    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 180);
    });

    this.form?.addEventListener('submit', () => {
      if (this.queryField) this.queryField.value = this.scopedQuery(this.input?.value.trim() || '');
    });
  }

  scopedQuery(query) {
    return buildScopedProductQuery(query, this.dataset.productTypes || '');
  }

  normalizeSearchValue(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  levenshteinDistance(a, b) {
    const rows = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
      let previous = rows[0];
      rows[0] = i;
      for (let j = 1; j <= b.length; j += 1) {
        const current = rows[j];
        rows[j] = Math.min(
          rows[j] + 1,
          rows[j - 1] + 1,
          previous + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
        previous = current;
      }
    }
    return rows[b.length];
  }

  fuzzyScore(query, product) {
    const needle = this.normalizeSearchValue(query);
    const words = this.normalizeSearchValue((product.title || '') + ' ' + (product.vendor || ''))
      .split(' ')
      .filter(Boolean);

    if (!needle || !words.length) return Number.POSITIVE_INFINITY;
    if (words.some((word) => word.includes(needle))) return 0;
    return Math.min(...words.map((word) => this.levenshteinDistance(needle, word)));
  }

  relevanceScore(query, product) {
    const needle = this.normalizeSearchValue(query);
    const title = this.normalizeSearchValue(product.title || '');
    const vendor = this.normalizeSearchValue(product.vendor || '');
    const titleWords = title.split(' ').filter(Boolean);

    if (!needle || !title) return Number.POSITIVE_INFINITY;
    if (title === needle) return 0;
    if (title.startsWith(needle)) return 1;
    if (titleWords.some((word) => word.startsWith(needle))) return 2;
    if (title.includes(needle)) return 3;
    if (vendor.startsWith(needle)) return 4;
    if (vendor.includes(needle)) return 5;

    const searchableWords = [...titleWords, ...vendor.split(' ').filter(Boolean)];
    const bestDistance = Math.min(...searchableWords.map((word) => this.levenshteinDistance(needle, word)));
    return 10 + bestDistance;
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const searchUrl = window.Shopify.routes.root + 'search?q=' + encodeURIComponent(this.scopedQuery(query)) + '&type=product&options%5Bprefix%5D=last';
      const response = await fetch(searchUrl, { signal: this.abortController.signal });
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const products = Array.from(documentFragment.querySelectorAll('.product-card'))
        .slice(0, 6)
        .map((card) => {
          const titleLink = card.querySelector('.product-card__title');
          const image = card.querySelector('.product-card__media img');
          return {
            title: titleLink?.textContent?.trim() || '',
            vendor: card.querySelector('.product-card__vendor')?.textContent?.trim() || '',
            price: card.querySelector('.product-card__price')?.textContent?.trim() || '',
            url: titleLink?.getAttribute('href') || '',
            image: image?.getAttribute('src') || ''
          };
        })
        .filter((product) => product.title && product.url);

      let editorialImages = {};
      try {
        editorialImages = JSON.parse(this.querySelector('[data-editorial-search-images]')?.textContent || '{}');
      } catch (error) {
        editorialImages = {};
      }

      this.results.innerHTML = products.length
        ? products.map((product) => {
          const handle = product.url.split('/products/')[1]?.split(/[?#]/)[0] || '';
          const image = editorialImages[handle] || product.image;
          return `
          <a class="predictive-search__item" href="${product.url}">
            ${image ? `<img src="${image}" alt="${product.title}" width="72" height="72" loading="lazy">` : ''}
            <span>
              <small>${product.vendor}</small>
              <strong>${product.title}</strong>
              <span>${product.price}</span>
            </span>
          </a>
        `;
        }).join('')
        : '<p class="predictive-search__empty">Não encontrámos resultados. Experimente escrever apenas parte do nome ou da marca.</p>';
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    }
  }}

customElements.define('ea-search', EASearch);

document.querySelectorAll('[data-scoped-search-form]').forEach((form) => {
  const input = form.querySelector('[data-search-input]');
  const queryField = form.querySelector('[data-search-query]');

  form.addEventListener('submit', () => {
    if (!input || !queryField) return;
    queryField.value = buildScopedProductQuery(input.value.trim(), form.dataset.productTypes || '');
  });
});

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-sort-select]')) {
    event.target.form?.submit();
  }
});

class EAProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!this.main) return;
        this.main.src = button.dataset.image;
        this.main.srcset = '';
        this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb === button);
          thumb.setAttribute('aria-pressed', thumb === button ? 'true' : 'false');
        });
      });
    });
  }
}

customElements.define('ea-product-gallery', EAProductGallery);

class EAProductForm extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-variant-select]');
    this.price = document.querySelector('[data-product-price]');
    this.compare = document.querySelector('[data-product-compare]');
    this.stock = document.querySelector('[data-stock-status]');
    this.stockLabel = this.stock?.querySelector('[data-stock-label]');
    this.button = this.querySelector('[data-add-to-cart]');
    this.buttonText = this.querySelector('[data-add-to-cart-text]');
    this.select?.addEventListener('change', () => this.update());
    this.querySelector('form')?.addEventListener('submit', (event) => this.addToCart(event));
    if (this.select) this.update();
  }

  update() {
    const option = this.select.selectedOptions[0];
    const available = option.dataset.available === 'true';
    const inventory = Number(option.dataset.inventory || 0);
    const inventoryTracked = option.dataset.inventoryTracked === 'true';
    const continueSelling = option.dataset.inventoryPolicy === 'continue';
    let stockStatus = 'in-stock';
    const strings = window.EssenciaArabeStrings || {};
    let stockLabel = strings.inStock || 'Em stock';

    if (!available) {
      stockStatus = 'sold-out';
      stockLabel = strings.soldOut || 'Esgotado';
    } else if (inventoryTracked && !continueSelling && inventory <= 3) {
      stockStatus = 'low-stock';
      stockLabel = strings.lowStock || 'Stock reduzido';
    }

    this.price.textContent = option.dataset.price;
    this.compare.textContent = option.dataset.compare;
    this.compare.hidden = !option.dataset.compare || option.dataset.compare === option.dataset.price;
    if (this.stock) {
      this.stock.classList.remove('product-stock--in-stock', 'product-stock--low-stock', 'product-stock--sold-out');
      this.stock.classList.add(`product-stock--${stockStatus}`);
    }
    if (this.stockLabel) this.stockLabel.textContent = stockLabel;
    this.button.disabled = !available;
    this.buttonText.textContent = available ? (strings.addToCart || 'Adicionar ao carrinho') : (strings.soldOut || 'Esgotado');
  }

  async addToCart(event) {
    const drawer = document.querySelector('ea-cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    const variantInput = this.select || this.querySelector('input[name="id"]');
    if (!variantInput?.value) return;
    this.button.disabled = true;
    const strings = window.EssenciaArabeStrings || {};
    this.buttonText.textContent = strings.adding || 'A adicionar…';

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantInput.value),
            quantity: Number(event.currentTarget.elements.quantity?.value || 1)
          }]
        })
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || strings.addError || 'Não foi possível adicionar o produto.');
      }
      await drawer.refresh();
      drawer.open();
      this.buttonText.textContent = strings.added || 'Adicionado';
      window.setTimeout(() => {
        this.button.disabled = false;
        this.buttonText.textContent = strings.addToCart || 'Adicionar ao carrinho';
      }, 1200);
    } catch (error) {
      console.error('Essência Árabe — erro ao adicionar ao carrinho:', error);
      this.button.disabled = false;
      this.buttonText.textContent = strings.tryAgain || 'Tentar novamente';
    }
  }
}

customElements.define('ea-product-form', EAProductForm);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
      })
      .catch(() => {});
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class EACoverflow extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-coverflow-card]'));
    this.previousButton = this.querySelector('[data-coverflow-previous]');
    this.nextButton = this.querySelector('[data-coverflow-next]');
    this.dots = this.querySelector('[data-coverflow-dots]');
    this.active = Math.min(1, Math.max(0, this.cards.length - 1));
    this.startX = null;
    this.autoPlayDelay = 5200;

    this.cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute(
        'aria-label',
        (window.EssenciaArabeStrings?.viewFeature || 'View feature {{ number }}').replace('{{ number }}', index + 1)
      );
      dot.addEventListener('click', () => {
        this.active = index;
        this.render();
        this.animateActiveCard();
      });
      this.dots?.appendChild(dot);
    });

    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.move(-1);
      if (event.key === 'ArrowRight') this.move(1);
    });
    this.addEventListener('pointerdown', (event) => {
      this.startX = event.clientX;
    });
    this.addEventListener('pointerup', (event) => {
      if (this.startX === null) return;
      const distance = event.clientX - this.startX;
      this.startX = null;
      if (Math.abs(distance) > 45) this.move(distance > 0 ? -1 : 1);
    });
    this.addEventListener('mouseenter', () => this.stopAutoPlay());
    this.addEventListener('mouseleave', () => this.startAutoPlay());
    this.addEventListener('focusin', () => this.stopAutoPlay());
    this.addEventListener('focusout', () => this.startAutoPlay());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoPlay();
      else this.startAutoPlay();
    });
    this.render();
    this.startAutoPlay();
  }

  move(direction) {
    if (!this.cards.length) return;
    this.active = (this.active + direction + this.cards.length) % this.cards.length;
    this.render();
    this.animateActiveCard();
  }

  animateActiveCard() {
    const activeCard = this.cards[this.active];
    if (!activeCard || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    activeCard.classList.remove('is-entering');
    void activeCard.offsetWidth;
    activeCard.classList.add('is-entering');
    window.clearTimeout(this.animationTimer);
    this.animationTimer = window.setTimeout(() => activeCard.classList.remove('is-entering'), 760);
  }

  startAutoPlay() {
    this.stopAutoPlay();
    if (this.cards.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.autoPlayTimer = window.setInterval(() => this.move(1), this.autoPlayDelay);
  }

  stopAutoPlay() {
    window.clearInterval(this.autoPlayTimer);
  }

  disconnectedCallback() {
    this.stopAutoPlay();
    window.clearTimeout(this.animationTimer);
  }

  render() {
    const total = this.cards.length;
    const dots = Array.from(this.dots?.children || []);
    this.cards.forEach((card, index) => {
      let offset = index - this.active;
      if (offset > total / 2) offset -= total;
      if (offset < -total / 2) offset += total;
      const distance = Math.abs(offset);
      card.style.setProperty('--coverflow-x', `${offset * 72}%`);
      card.style.setProperty('--coverflow-scale', String(Math.max(.7, 1 - distance * .12)));
      card.style.setProperty('--coverflow-rotate', `${offset * -8}deg`);
      card.style.setProperty('--coverflow-opacity', distance > 2 ? '0' : String(Math.max(.42, 1 - distance * .25)));
      card.style.zIndex = String(20 - distance);
      card.classList.toggle('is-active', index === this.active);
      card.setAttribute('aria-hidden', distance > 2 ? 'true' : 'false');
      if (index === this.active) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
      if (dots[index]) dots[index].setAttribute('aria-current', index === this.active ? 'true' : 'false');
    });
  }
}

customElements.define('ea-coverflow', EACoverflow);

class EACartDrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector('[aria-controls="CartDrawer"]');
    this.closeButton = this.querySelector('[data-cart-close]');
    this.backdrop = this.querySelector('[data-cart-backdrop]');
    this.items = this.querySelector('[data-cart-items]');
       this.empty = this.querySelector('[data-cart-empty]');
    this.footer = this.querySelector('[data-cart-footer]');
    this.loading = this.querySelector('[data-cart-loading]');
    this.total = this.querySelector('[data-cart-total]');
    this.message = this.querySelector('[data-shipping-message]');
    this.progress = this.querySelector('[data-shipping-progress]');
    this.counts = document.querySelectorAll('[data-cart-count]');
    this.checkout = this.querySelector('.cart-drawer__checkout');
    this.decantMinimum = document.createElement('div');
    this.decantMinimum.id = 'EaDrawerDecantMinimum';
    this.decantMinimum.setAttribute('role', 'alert');
    this.decantMinimum.style.cssText = 'margin:14px 0;padding:14px;border:1px solid #c99722;background:rgba(201,151,34,.10);color:inherit;';
    this.decantMinimum.hidden = true;
    this.checkout?.insertAdjacentElement('beforebegin', this.decantMinimum);

    this.trigger?.addEventListener('click', async () => {
      await this.refresh();
      this.open();
    });
    this.closeButton?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    this.addEventListener('click', (event) => {
      const control = event.target.closest('[data-cart-change]');
      if (control) this.change(control.dataset.key, Number(control.dataset.quantity));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');
    this.closeButton?.focus();
  }

  close() {
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.trigger?.focus();
  }

  money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(cents / 100);
  }

  async refresh() {
    this.loading.hidden = false;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      this.render(cart);
    } finally {
      this.loading.hidden = true;
    }
  }

  async change(key, quantity) {
    this.loading.hidden = false;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await response.json();
      this.render(cart);
    } finally {
      this.loading.hidden = true;
    }
  }

  render(cart) {
    const strings = window.EssenciaArabeStrings || {};
    this.items.innerHTML = cart.items.map((item) => `
      <article class="cart-item">
        <a class="cart-item__image" href="${item.url}">
          ${item.image ? `<img src="${item.image}" alt="" width="160" height="200">` : ``}
        </a>
        <div class="cart-item__content">
          <small>${item.vendor || ``}</small>
          <a href="${item.url}"><strong>${item.product_title}</strong></a>
          ${item.variant_title ? `<span>${item.variant_title}</span>` : ``}
          <div class="cart-item__bottom">
            <div class="cart-quantity" aria-label="${strings.quantity || `Quantidade`}">
              <button type="button" data-cart-change data-key="${item.key}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${strings.decreaseQuantity || `Diminuir quantidade`}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change data-key="${item.key}" data-quantity="${item.quantity + 1}" aria-label="${strings.increaseQuantity || `Aumentar quantidade`}">+</button>
            </div>
            <strong>${this.money(item.final_line_price)}</strong>
          </div>
          <button class="cart-item__remove" type="button" data-cart-change data-key="${item.key}" data-quantity="0">${strings.remove || `Remover`}</button>
        </div>
      </article>
    `).join('');

    const isEmpty = cart.item_count === 0;
    this.empty.hidden = !isEmpty;
    this.footer.hidden = isEmpty;
    this.total.textContent = this.money(cart.total_price);
    this.counts.forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = isEmpty;
    });

    const decantQuantity = cart.items.reduce((total, item) => {
      const variantTitle = String(item.variant_title || '').toLowerCase().replace(/\s+/g, '');
      return total + (variantTitle === '5ml' ? item.quantity : 0);
    }, 0);
    const hasNonDecant = cart.items.some((item) => String(item.variant_title || '').toLowerCase().replace(/\s+/g, '') !== '5ml');
    const decantMinimumBlocked = !hasNonDecant && decantQuantity > 0 && decantQuantity < 3;

    if (this.checkout) {
      this.checkout.hidden = decantMinimumBlocked;
        this.checkout.style.display = decantMinimumBlocked ? 'none' : '';
        this.checkout.setAttribute('aria-disabled', decantMinimumBlocked ? 'true' : 'false');
        if (decantMinimumBlocked) {
          this.checkout.removeAttribute('href');
        } else {
          this.checkout.setAttribute('href', `${window.Shopify.routes.root}checkout`);
        }
      this.checkout.setAttribute('aria-hidden', decantMinimumBlocked ? 'true' : 'false');
    }
    if (this.decantMinimum) {
      const missing = 3 - decantQuantity;
      this.decantMinimum.hidden = !decantMinimumBlocked;
      this.decantMinimum.innerHTML = decantMinimumBlocked
        ? `<strong>Compra mínima de 3 decantes</strong><span style="display:block;margin-top:4px;font-size:.92em;">Adiciona mais ${missing} ${missing === 1 ? `decante` : `decantes`}. Podes combinar fragrâncias diferentes.</span><a href="${window.Shopify.routes.root}collections/decants-5-ml" style="display:inline-block;margin-top:9px;color:#e7bd54;text-decoration:underline;">Descobre os Discovery Set's</a>`
        : '';
    }

    const threshold = 10000;
    const remaining = Math.max(0, threshold - cart.total_price);
    const percent = Math.min(100, (cart.total_price / threshold) * 100);
    this.message.textContent = remaining > 0
      ? (strings.freeShippingRemaining || 'Faltam {{ amount }} para o envio gratuito.').replace('{{ amount }}', this.money(remaining))
      : (strings.freeShippingReached || 'Conquistou envio gratuito para Portugal Continental.');
    this.progress.style.width = `${percent}%`;
  }
}

customElements.define('ea-cart-drawer', EACartDrawer);
    